import type { RenderContext, SectionProps } from "../context";
import { field, messageVars } from "../context";
import { formatRupiah, waLink } from "../helpers";
import { Icon } from "../icons";
import { Container, CtaButton, REVEAL, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx, type CtaLike } from "../ui";

type Plan = SectionProps<"pricing">["node"]["content"]["plans"][number];

const COLS = ["", "", "@3xl:grid-cols-2", "@3xl:grid-cols-3", "@3xl:grid-cols-2 @5xl:grid-cols-4", "@3xl:grid-cols-3", "@3xl:grid-cols-3"] as const;

function planCta(plan: Plan): CtaLike {
  const cta = plan.cta ?? { label: "Pilih paket", kind: "whatsapp" as const };
  if (cta.kind === "whatsapp" && !cta.message) return { ...cta, message: `Halo {{nama_usaha}}, saya tertarik dengan paket ${plan.name}.` };
  return cta;
}

function PlanPrice({ ctx, plan, i, large }: { ctx: RenderContext; plan: Plan; i: number; large?: boolean }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
      {plan.priceNote ? (
        <span className="w-full text-sm font-medium text-site-muted" {...field(ctx, `content.plans.${i}.priceNote`)}>
          {plan.priceNote}
        </span>
      ) : null}
      {plan.price !== undefined ? (
        <span className={cx("baze-heading tabular-nums", large ? "text-4xl @3xl:text-[2.75rem]" : "text-2xl")} {...field(ctx, `content.plans.${i}.price`)}>
          {formatRupiah(plan.price)}
        </span>
      ) : (
        <span className={cx("baze-heading", large ? "text-3xl" : "text-xl")}>Tanya harga</span>
      )}
      {plan.period ? <span className="text-sm text-site-muted">{plan.period}</span> : null}
    </div>
  );
}

function AskLink({ ctx }: { ctx: RenderContext }) {
  const href = waLink(ctx.site.contact.whatsapp, "Halo {{nama_usaha}}, saya mau tanya soal paket.", messageVars(ctx));
  if (!href) return null;
  return (
    <p className="mt-10 text-center text-site-muted">
      Butuh paket khusus?{" "}
      <SiteLink ctx={ctx} href={href} whatsapp className="inline-flex min-h-11 items-center gap-1.5 font-semibold text-site-primary underline-offset-4 hover:underline">
        <Icon name="whatsapp" className="size-4" />
        Tanya via WhatsApp
      </SiteLink>
    </p>
  );
}

export function Pricing({ node, ctx }: SectionProps<"pricing">) {
  const c = node.content;
  const plans = c.plans;
  const header = <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align="center" />;

  if (node.variant === "table" && plans.length > 0) {
    const features: string[] = [];
    for (const p of plans) for (const f of p.features) if (!features.includes(f)) features.push(f);
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          {header}
          {/* relative: contains the table's absolutely positioned sr-only labels, which otherwise escape
              the scroll box and widen the page on phones. */}
          <div className={cx(STACK, REVEAL, "relative overflow-x-auto rounded-site-md border border-site-border bg-site-surface shadow-site-sm")}>
            <table className="w-full min-w-[36rem] border-collapse text-left">
              <caption className="sr-only">{c.title}</caption>
              <thead>
                <tr className="border-b border-site-border">
                  <th scope="col" className="w-2/5 p-5 text-sm font-medium text-site-muted @3xl:p-6">
                    Paket
                  </th>
                  {plans.map((p, i) => (
                    <th key={i} scope="col" className={cx("p-5 align-bottom @3xl:p-6", p.highlighted && "bg-site-primary/5")}>
                      {p.highlighted ? <span className="mb-2 inline-block rounded-full bg-site-primary px-2.5 py-0.5 text-xs font-semibold text-site-on-primary">Populer</span> : null}
                      <span className="block text-lg font-semibold text-site-text" {...field(ctx, `content.plans.${i}.name`)}>
                        {p.name}
                      </span>
                      <div className="mt-2">
                        <PlanPrice ctx={ctx} plan={p} i={i} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-site-border">
                {features.map((f) => (
                  <tr key={f}>
                    <th scope="row" className="p-5 text-[0.9375rem] font-normal text-site-text @3xl:px-6 @3xl:py-4">
                      {f}
                    </th>
                    {plans.map((p, i) => (
                      <td key={i} className={cx("p-5 @3xl:px-6 @3xl:py-4", p.highlighted && "bg-site-primary/5")}>
                        {p.features.includes(f) ? (
                          <span className="grid size-6 place-items-center rounded-full bg-site-primary text-site-on-primary">
                            <Icon name="check" className="size-3.5" />
                            <span className="sr-only">Termasuk</span>
                          </span>
                        ) : (
                          <span className="text-site-muted">
                            <span aria-hidden="true">—</span>
                            <span className="sr-only">Tidak termasuk</span>
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="p-5 @3xl:p-6" />
                  {plans.map((p, i) => (
                    <td key={i} className={cx("p-5 @3xl:p-6", p.highlighted && "bg-site-primary/5")}>
                      <CtaButton ctx={ctx} cta={planCta(p)} path={`content.plans.${i}.cta`} style={p.highlighted ? "primary" : "secondary"} className="min-h-11 px-4 text-sm" />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <AskLink ctx={ctx} />
        </Container>
      </SectionShell>
    );
  }

  const n = Math.min(plans.length, 6);
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        {header}
        <ul className={cx(STACK, STAGGER, "grid items-stretch gap-6", COLS[n], n === 1 && "mx-auto max-w-md", n === 2 && "mx-auto max-w-4xl")}>
          {plans.map((p, i) => (
            <li
              key={i}
              className={cx(
                "relative flex flex-col rounded-site-lg border bg-site-surface p-7 transition-[translate,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-site-lg motion-reduce:transition-none motion-reduce:hover:translate-y-0 @3xl:p-8",
                p.highlighted ? "border-site-primary shadow-site-lg ring-2 ring-site-primary" : "border-site-border shadow-site-sm",
              )}
            >
              {p.highlighted ? (
                <span className="absolute -top-3 left-7 rounded-full bg-site-primary px-3 py-1 text-xs font-semibold text-site-on-primary shadow-site-sm">Paling populer</span>
              ) : null}
              <h3 className="text-lg font-semibold text-site-text" {...field(ctx, `content.plans.${i}.name`)}>
                {p.name}
              </h3>
              <div className="mt-4">
                <PlanPrice ctx={ctx} plan={p} i={i} large />
              </div>
              <ul className="mt-7 flex-1 space-y-3 border-t border-site-border pt-7">
                {p.features.map((f, fi) => (
                  <li key={fi} className="flex gap-3 text-[0.9375rem] leading-snug text-site-text" {...field(ctx, `content.plans.${i}.features.${fi}`)}>
                    <Icon name="check" className="mt-0.5 size-4 shrink-0 text-site-primary" />
                    {f}
                  </li>
                ))}
              </ul>
              <CtaButton ctx={ctx} cta={planCta(p)} path={`content.plans.${i}.cta`} style={p.highlighted ? "primary" : "secondary"} className="mt-8 w-full" />
            </li>
          ))}
        </ul>
        <AskLink ctx={ctx} />
      </Container>
    </SectionShell>
  );
}
